/** One entry of the page bar: a page number or a gap ("…"). */
export type PageItem = number | "gap-left" | "gap-right";

/**
 * Page numbers to render: always the first and last page, the current page
 * with `siblings` pages on each side, and a gap where pages are skipped.
 * A gap never stands in for a single page (that page is shown instead).
 */
export const buildPageItems = (
  currentPage: number,
  lastPage: number,
  siblings = 2,
): PageItem[] => {
  const last = Math.max(1, Math.floor(lastPage) || 1);
  const current = Math.min(Math.max(1, Math.floor(currentPage) || 1), last);
  if (last <= siblings * 2 + 5) {
    return Array.from({ length: last }, (_, index) => index + 1);
  }
  const start = Math.max(2, current - siblings);
  const end = Math.min(last - 1, current + siblings);

  const items: PageItem[] = [1];
  if (start > 3) items.push("gap-left");
  else if (start === 3) items.push(2);
  for (let page = start; page <= end; page += 1) items.push(page);
  if (end < last - 2) items.push("gap-right");
  else if (end === last - 2) items.push(last - 1);
  if (last > 1) items.push(last);
  return items;
};

/** A typed page number clamped to 1..lastPage, or null when it is not a number. */
export const parsePageInput = (value: string, lastPage: number): number | null => {
  const page = Number.parseInt(value.trim(), 10);
  if (!Number.isFinite(page)) return null;
  return Math.min(Math.max(1, page), Math.max(1, lastPage));
};

/** The page sizes offered by default; the current size is always added to them. */
export const DEFAULT_PER_PAGE_OPTIONS = [10, 15, 25, 50];

/** The print-shop queues: up to 100 rows, the most DS serves per page. */
export const PRINT_SHOP_PER_PAGE_OPTIONS = [10, 25, 50, 100];

/**
 * Sorted, de-duplicated page sizes that always include the current one, so the
 * select can never fall back to a placeholder for a size it does not list.
 */
export const buildPerPageOptions = (
  currentPerPage: number,
  defaults: number[] = DEFAULT_PER_PAGE_OPTIONS,
): number[] => {
  const sizes = new Set(defaults);
  if (Number.isFinite(currentPerPage) && currentPerPage > 0) sizes.add(Math.floor(currentPerPage));
  return Array.from(sizes).sort((a, b) => a - b);
};
